import { useState } from 'react';
import { ChevronDown, Smartphone } from 'lucide-react';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@/constants/appStores';
import { cn } from '@/utils/cn';

function GooglePlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="14 12 46 46" className={className} aria-hidden focusable="false">
      <path fill="#4285F4" d="m17.7 16.4-.1 1.4V53l.1 1.3L37.2 35 17.7 16.4Z" />
      <path fill="#34A853" d="m36.7 35.4 9.8-9.7-21.2-12.3a5.1 5.1 0 0 0-7.6 3s19 19 19 19Z" />
      <path
        fill="#FBBC04"
        d="m55.7 31-9.2-5.3-10.3 9.2 10.3 10.3 9.1-5.2a5 5 0 0 0 0-9Z"
      />
      <path
        fill="#EA4335"
        d="m36.6 34.4-18.9 20a5 5 0 0 0 7.5 3.1h.1l21.2-12.3-10-10.8Z"
      />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden
      focusable="false"
      fill="currentColor"
    >
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

const buttonBadgeClass =
  'flex h-11 w-full items-center gap-2.5 rounded-[10px] border border-white/25 bg-black px-3 text-white transition hover:bg-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 sm:h-12 sm:gap-3 sm:px-3.5';

function StoreLinks({ className }: { className?: string }) {
  return (
    <div className={cn('grid w-full grid-cols-1 gap-2', className)}>
      <a
        href={GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonBadgeClass}
        aria-label="Get it on Google Play"
      >
        <GooglePlayIcon className="h-7 w-7 shrink-0 sm:h-8 sm:w-8" />
        <span className="min-w-0 leading-tight">
          <span className="block text-[8px] font-medium uppercase tracking-[0.06em] text-white/90 sm:text-[9px]">
            Get it on
          </span>
          <span className="block text-[13px] font-semibold tracking-tight sm:text-sm">
            Google Play
          </span>
        </span>
      </a>

      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonBadgeClass}
        aria-label="Download on the App Store"
      >
        <AppleIcon className="h-7 w-7 shrink-0 text-white sm:h-8 sm:w-8" />
        <span className="min-w-0 leading-tight">
          <span className="block text-[8px] font-medium tracking-wide text-white/90 sm:text-[9px]">
            Download on the
          </span>
          <span className="block text-[13px] font-semibold tracking-tight sm:text-sm">
            App Store
          </span>
        </span>
      </a>
    </div>
  );
}

/** Official badge artwork (SVG) — best for light backgrounds / landing. */
export function StoreBadgeImages({
  className,
  size = 'md',
  fullWidth = false,
}: {
  className?: string;
  size?: 'sm' | 'md';
  /** Stretch both badges to the same container width. */
  fullWidth?: boolean;
}) {
  const height = size === 'sm' ? 'h-10' : 'h-12';
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3',
        fullWidth && 'w-full flex-col items-stretch gap-2',
        className,
      )}
    >
      <a
        href={GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
          fullWidth && 'block w-full',
        )}
        aria-label="Get it on Google Play"
      >
        <img
          src="/badges/google-play.svg"
          alt="Get it on Google Play"
          className={cn(fullWidth ? 'h-auto w-full' : cn(height, 'w-auto'))}
        />
      </a>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
          fullWidth && 'block w-full',
        )}
        aria-label="Download on the App Store"
      >
        <img
          src="/badges/app-store.svg"
          alt="Download on the App Store"
          className={cn(fullWidth ? 'h-auto w-full' : cn(height, 'w-auto'))}
        />
      </a>
    </div>
  );
}

/** Stacked black store buttons for the dashboard sidebar. */
export function SidebarStoreBadges({ className }: { className?: string }) {
  return (
    <div className={cn('w-full', className)}>
      <p className="mb-2 px-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        Get our app
      </p>
      <StoreLinks />
    </div>
  );
}

/**
 * Mobile Overview: compact “Get the App” control that expands to store badges.
 * Hidden on lg+ where the sidebar already shows them.
 */
export function GetTheAppDisclosure({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn('relative shrink-0 lg:hidden', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10"
      >
        <Smartphone className="h-3.5 w-3.5" />
        Get the App
        <ChevronDown
          className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-[11.5rem] rounded-2xl border border-gray-100 bg-white p-2.5 shadow-card">
          <p className="mb-2 text-[11px] leading-snug text-brand-muted">
            Install ASDATALINK for faster top-ups on the go.
          </p>
          <StoreBadgeImages fullWidth size="sm" />
        </div>
      )}
    </div>
  );
}
